import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AiInterviewRole } from '@edtech-ui/core/models/ai-interview/getroles';
import { AiInterviewRound } from '@edtech-ui/core/models/ai-interview/interview-round';
import { AiInterviewServiceService } from '@edtech-ui/core/services/ai-interview-service/ai-interview-service.service';
import { ToastrService } from 'ngx-toastr';
import { RoleCreateDailogComponent } from '../../role-create-dailog/role-create-dailog.component';
import { AiInterviewSessionService } from '@edtech-ui/core/services/ai-interview-session-service/ai-interview-session.service';
import { catchError, of, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AiInterviewLimitDialogBoxComponent } from '@edtech-ui/student-dashboard-v2/ai-interview-limit-dialog-box/ai-interview-limit-dialog-box.component';
import { AiInterviewRoundsPopupComponent } from '@edtech-ui/student-dashboard-v2/ai-interview-rounds-popup/ai-interview-rounds-popup.component';

interface UiInterviewRound extends AiInterviewRound {
	key: string;
	icon: string;
}

@Component({
	selector: 'app-step-setup-interview',
	standalone: true,
	imports: [CommonModule, FormsModule, RoleCreateDailogComponent],
	templateUrl: './step-setup-interview.component.html',
	styleUrls: ['./step-setup-interview.component.scss']
})
export class StepSetupInterviewComponent {
	@Output() continue = new EventEmitter<void>();

	isDropdownOpen = false;
	searchTerm = '';
	selectedRole: AiInterviewRole | null = null;
	selectedRound: UiInterviewRound | null = null;
	roles: AiInterviewRole[] = [];
	filteredRoles: AiInterviewRole[] = [];
	rounds: UiInterviewRound[] = [];
	loading = false;
	roundsLoading = false;
	showCreateRoleModal = false;
	availableTime = 0;

	constructor(
		private toast: ToastrService,
		private aiInterviewService: AiInterviewServiceService,
		private session: AiInterviewSessionService,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.loadRoles();
		this.loadInterviewRounds();

		const saved = this.session.value;
		console.log('Saved session state:', saved);
		if (saved.role_id) {
			this.selectedRole = { id: saved.role_id, title: saved.role_title || '' } as AiInterviewRole;
			this.searchTerm = saved.role_title || '';
		}

		this.restoreRoundAfterLoad(saved);
	}

	private restoreRoundAfterLoad(saved: any) {
		const check = setInterval(() => {
			if (!this.roundsLoading && this.rounds.length > 0) {
				clearInterval(check);
				if (saved.interview_round) {
					this.selectedRound =
						this.rounds.find(r => r.name === saved.interview_round) || null;
				}
			}
		}, 200);
	}

	loadRoles(): void {
		this.loading = true;
		this.aiInterviewService.getRoles().subscribe({
			next: (response) => {
				this.loading = false;
				if (response?.Success && response.Data?.length) {
					this.roles = response.Data;
					this.filteredRoles = [...response.Data];
				} else {
					this.toast.warning(response?.Message || 'No roles found.');
				}
			},
			error: () => {
				this.loading = false;
				this.toast.error('Failed to load roles. Please try again later.');
			},
		});
	}

	loadInterviewRounds(): void {
		this.roundsLoading = true;

		this.aiInterviewService.getInterviewRounds().pipe(
			tap((response) => {
				this.roundsLoading = false;

				if (response?.Success && response.Data?.rounds?.length) {


					this.rounds = response.Data.rounds.map((r: AiInterviewRound) => this.mapRound(r));

					this.availableTime = response.Data.available_time;
					this.aiInterviewService.setAvailableTime(this.availableTime);

					const saved = this.session.value;
					if (saved.interview_round_id) {
						this.selectedRound =
							this.rounds.find(r => r.id === saved.interview_round_id) || null;
					}
				} else {
					this.toast.warning(response?.Message || 'No interview rounds found.');
				}
			}),
			catchError((error) => {
				this.roundsLoading = false;
				this.toast.error('Failed to load interview rounds.');
				return of(null);
			})
		).subscribe();
	}

	private mapRound(r: AiInterviewRound): UiInterviewRound {
		const name = r.name.toLowerCase();
		let key = '';
		let icon = '';

		if (name.includes('warm')) {
			key = 'warmup';
			icon = 'bi bi-lightning-charge-fill';
		} else if (name.includes('technical')) {
			key = 'technical';
			icon = 'bi bi-code';
		} else if (name.includes('managerial')) {
			key = 'managerial';
			icon = 'bi bi-people-fill';
		} else if (name.includes('hr')) {
			key = 'hr';
			icon = 'bi bi-person-badge-fill';
		} else {
			key = 'other';
			icon = 'bi bi-soundwave';
		}

		return {
			...r, key, icon,
			is_available_for_user: r.is_available_for_user
		};
	}

	openDropdown(event?: MouseEvent) {
		if (event) event.stopPropagation();
		this.isDropdownOpen = true;
	}

	filterRoles() {
		const search = this.searchTerm.toLowerCase();
		this.filteredRoles = this.roles.filter((r) =>
			r.title.toLowerCase().includes(search)
		);
	}

	selectRole(role: AiInterviewRole, event?: MouseEvent) {
		if (event) event.stopPropagation();
		this.selectedRole = role;
		this.searchTerm = role.title;
		this.isDropdownOpen = false;
	}

	selectRound(round: UiInterviewRound) {
		if (!round.is_available_for_user) {
			this.openAiInterviewRoundDialogBox();
			return;
		}
		this.selectedRound = round;
	}

	onContinue() {
		if (!this.selectedRole || !this.selectedRound) {
			this.toast.warning('Please select both role and round to continue.');
			return;
		}

		if (this.availableTime === 0) {
			this.openAvailableTimeLimitDialogBox();
			return;
		}

		this.session.update({
			role_id: this.selectedRole.id,
			role_title: this.selectedRole.title,
			interview_round: this.selectedRound.name,
			interview_round_id: this.selectedRound.id
		});

		this.continue.emit();

	}

	createNewRole() {
		this.showCreateRoleModal = true;
	}

	handleRoleCreated(newRole: AiInterviewRole) {
		this.roles.push(newRole);
		this.filteredRoles = [...this.roles];
		this.selectRole(newRole);
		this.showCreateRoleModal = false;
	}

	closeCreateRoleModal() {
		this.showCreateRoleModal = false;
	}

	@HostListener('document:click', ['$event'])
	handleOutsideClick(event: MouseEvent) {
		const target = event.target as HTMLElement;
		const dropdownClicked = target.closest('.dropdown-container') !== null;
		if (!dropdownClicked) this.isDropdownOpen = false;
	}


	openAvailableTimeLimitDialogBox() {
		this.dialog.open(AiInterviewLimitDialogBoxComponent)
	}

	openAiInterviewRoundDialogBox() {
		this.dialog.open(AiInterviewRoundsPopupComponent)
	}

}
